import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { motion, AnimatePresence } from 'framer-motion';

import NoDataCreated from "../../../../../Components/NoDataCreated";
import TextInput from "../../../../../Components/TextInput";
import ErrorPage from "../../../../../Components/ErrorPage";
import { goToTop } from "../../../../../Components/goToTop";
import Textarea from "../../../../../Components/TextArea";
import { checkIsArchitect, checkIsBuildingInspector } from "../../../../../Utils/LoginUserRoleName";

import { PlanCheckTracker, PlanCheckTrackerItemList, PlanCheckTrackerPart } from "../../../../../Controllers/Types/PlanCheck/PlanCheckTrackerType";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";
import { getAllPlanChckTrackerList } from "../../../../../Controllers/Thunks/PlanCheckTrackerThunks";
import { checkEmail, checkText } from "../../../../../Utils/Validator";
import { createPlanCheckItem, createPlanCheckPart, createPlanCheckTrackerResponseByBi, createPlanCheckTrackerResponseByArc, deletePlanCheckFile, deletePlanCheckItem, deletePlanCheckPart, responsePlanCheck, sharePlanCheck, updatePlanCheckItem, updatePlanCheckPart, uploadPlanCheckFiles } from "../../../../../Api/PlanCheck/PlanCheckApiServices";
import { InputText } from "primereact/inputtext";
import { documentFileExtension, imageFileExtension } from "../../../../../Api/FileConstant";
import { RadioButton, RadioButtonChangeEvent } from "primereact/radiobutton";
import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import CustomTableLoader from "../../../../../Components/CustomTableLoader";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import TableActionLoader from "../../../../../Components/TableActionLoader";
import FileActionLoader from "../../../../../Components/FileActionLoader";
import GoBackButton from "../../../../../Components/GoBackButton";
import ImagePreview from "../../../../../Components/ImagePreview";

const PlanCheckTrackerDetail: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const projectData = location.state?.projectData;
    const serviceId = Number(projectData.folder_id);
    const toast = useRef<Toast>(null);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [loading, setLoading] = useState<boolean>(false);
    const [saveNextLoading, setSaveNextLoading] = useState<boolean>(false);

    const dataLoading = useAppSelector((state) => state?.planCheckTrackerList?.loader);
    const error = useAppSelector((state) => state?.planCheckTrackerList?.errorDisplay);
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [responseFileDeleteLoadingId, setResponseFileDeleteLoadingId] = useState<number | null>(null);

    const [planCheckTrackerData, setPlanCheckTrackerData] = useState<PlanCheckTracker | null>(null);

    const [isSignedAndPublishedByArc, setIsSignedAndPublishedByArc] = useState<boolean>(false);
    const [isSignedAndPublishedByBi, setIsSignedAndPublishedByBi] = useState<boolean>(false);
    const [isResponseCreated, setIsResponseCreated] = useState<boolean>(false);
    const [isAllSatisfied, setIsAllSatisfied] = useState<boolean>(false);

    /* For Part */
    const [planCheckTrackerPartData, setPlanCheckTrackerPartData] = useState<PlanCheckTrackerPart[]>([]);
    const [selectedPartData, setSelectedPartData] = useState<PlanCheckTrackerPart | null>(null);
    const [selectedPartId, setSelectedPartId] = useState<number | null>(null);
    const [activePartData, setActivePartData] = useState<PlanCheckTrackerPart | null>(null);
    const [showPartModal, setShowPartModal] = useState<boolean>(false);
    const [partNumber, setPartNumber] = useState<string>('');
    const [partTitle, setPartTitle] = useState<string>('');
    const [partNumberError, setPartNumberError] = useState<string>('');
    const [partTitleError, setPartTitleError] = useState<string>('');

    const [partUpdateHasChanges, setPartUpdateHasChanges] = useState<boolean>(false);
    const [itemUpdateHasChanges, setItemUpdateHasChanges] = useState<boolean>(false);
    /*  */

    /* For Item */
    const [selectedItemData, setSelectedItemData] = useState<PlanCheckTrackerItemList | null>(null);
    const [showItemModal, setShowItemModal] = useState<boolean>(false);
    const [itemCode, setItemCode] = useState<string>('');
    const [comment, setComment] = useState<string>('');
    const [itemCodeError, setItemCodeError] = useState<string>('');
    const [commentError, setCommentError] = useState<string>('');
    /*  */

    /* For architect response */
    const [showArcReplyModal, setShowArcReplyModal] = useState<boolean>(false);
    const [arcResponseNote, setArcResponseNote] = useState<string>('');
    const [arcResponseFiles, setArcResponseFiles] = useState<File[]>([]);
    const [arcResponseNoteError, setArcResponseNoteError] = useState<string>('');
    const [arcResponseFileError, setArcResponseFileError] = useState<string>('');
    const [arcResponseUpdateHasChanges, setArcResponseUpdateHasChanges] = useState<boolean>(false);

    /* For building inspector response */
    const [showBiReplyModal, setShowBiReplyModal] = useState<boolean>(false);
    const [biResponseNote, setBiResponseNote] = useState<string>('');
    const [biResponseStatus, setBiResponseStatus] = useState<"Satisfactory" | "Unsatisfactory">('Unsatisfactory');
    const [biResponseNoteError, setBiResponseNoteError] = useState<string>('');
    const [biResponseStatusError, setBiResponseStatusError] = useState<string>('');
    const [biResponseUpdateHasChanges, setBiResponseUpdateHasChanges] = useState<boolean>(false);

    const [documentFilePath, setDocumentFilePath] = useState<string>("");
    const [showItemDocViewModal, setShowItemDocViewModal] = useState<boolean>(false);

    /* Mail */
    const [showMailModal, setShowMailModal] = useState<boolean>(false);
    const [emailAddress, setEmailAddress] = useState<string>("");
    const [emailAddressError, setEmailAddressError] = useState<string>("");

    /* Download */
    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const hasPermissionDownloadFile = useCanEdit('Plan Check Tracker', 'download_file');
    const hasPermissionAddSignature = useCanEdit("Plan Check Tracker", "add_signature");

    /* Check role */
    const [isInspector, setIsInspector] = useState<boolean>(false);
    const [isArchitect, setIsArchitect] = useState<boolean>(false);

    useEffect(() => {
        const checkRole = async () => {
            const [inspectorResult, architectResult] = await Promise.all([
                checkIsBuildingInspector(),
                checkIsArchitect(),
            ]);

            setIsInspector(inspectorResult);
            setIsArchitect(architectResult);
        };

        checkRole();
    }, []);
    /*  */


    // for fetching data fro api tigger with state object actions
    useEffect(() => {
        planCheckTrackerDataGet();
    }, [location.state]);

    const planCheckTrackerDataGet = async () => {
        await dispatch(getAllPlanChckTrackerList(serviceId));
        setInitialDataLoading(false);
    }

    // this is get data from state list and set projectlist
    const plancheckTrackerList = useAppSelector((state) => state?.planCheckTrackerList?.planCheckTrackerList);

    useEffect(() => {
        if (location.state && location.state.data) {
            const options = plancheckTrackerList && plancheckTrackerList.length > 0;
            const data = location.state.data as PlanCheckTracker;
            if (options) {
                const collect = plancheckTrackerList.filter((item) => item.id === data.id);
                if (collect && collect.length > 0) {
                    setPlanCheckTrackerData(collect[0]);
                }
            }
        }
    }, [location.state, plancheckTrackerList]);


    useEffect(() => {
        if (planCheckTrackerData && planCheckTrackerData !== null) {
            if (planCheckTrackerData?.is_sign_report_by_arc && planCheckTrackerData?.is_published_by_arc) {
                setIsSignedAndPublishedByArc(true);
            } else {
                setIsSignedAndPublishedByArc(false);
            }

            if (planCheckTrackerData?.is_sign_report_by_bi && planCheckTrackerData?.is_published_by_bi) {
                setIsSignedAndPublishedByBi(true);
            } else {
                setIsSignedAndPublishedByBi(false);
            }

            if (planCheckTrackerData?.is_created_responce !== null) {
                setIsResponseCreated(true);
            } else {
                setIsResponseCreated(false);
            }

            const unSatisfiedDetailItemsPart = planCheckTrackerData?.plan_check_part?.filter((part) => part.item_list?.some((item) => item.status === 'Open'));

            if (unSatisfiedDetailItemsPart?.length === 0 && planCheckTrackerData?.check_type === 'Detail') {
                setIsAllSatisfied(true);
            } else {
                setIsAllSatisfied(false);
            }
        } else {
            setIsSignedAndPublishedByArc(false);
            setIsSignedAndPublishedByBi(false);
        }
    }, [planCheckTrackerData]);


    useEffect(() => {
        if (planCheckTrackerData && planCheckTrackerData !== null) {
            if (planCheckTrackerData?.check_type === 'Detail' && planCheckTrackerData?.plan_check_part && planCheckTrackerData?.plan_check_part?.length > 0) {
                setPlanCheckTrackerPartData(planCheckTrackerData?.plan_check_part);
            } else {
                setPlanCheckTrackerPartData([]);
            }
        } else {
            setPlanCheckTrackerPartData([]);
        }
    }, [planCheckTrackerData]);

    useEffect(() => {
        if (planCheckTrackerPartData && planCheckTrackerPartData?.length > 0) {
            if (selectedPartData && selectedPartData !== null) {
                setActivePartData(selectedPartData);
            } else {
                setActivePartData(planCheckTrackerPartData?.[0]);
            }
        } else {
            setActivePartData(null);
        }
    }, [planCheckTrackerPartData, selectedPartData])


    useEffect(() => {
        if (selectedItemData && selectedItemData !== null) {
            if (comment !== selectedItemData?.comment) {
                setItemUpdateHasChanges(true);
            } else {
                setItemUpdateHasChanges(false);
            }
        }

        if (selectedPartId && selectedPartId !== null) {
            const updatingData = planCheckTrackerPartData?.find(part => part?.id === selectedPartId);

            if (partNumber !== updatingData?.part_number) {
                setPartUpdateHasChanges(true);
            } else if (partTitle !== updatingData?.part_title) {
                setPartUpdateHasChanges(true);
            } else {
                setPartUpdateHasChanges(false);
            }
        }
    }, [selectedItemData, comment, selectedPartId, partNumber, partTitle, planCheckTrackerPartData]);


    /* **************************** plan check tracker part ****************************** */
    const handleClosePartModal = () => {
        setShowPartModal(false);
        handleResetPartFields();
    }

    const handleResetPartFields = () => {
        setPartNumber('');
        setPartTitle('');
        setPartNumberError('');
        setPartTitleError('');
        setSelectedPartId(null);
    }

    const handleAddPart = () => {
        setDataState('Add');
        setShowPartModal(true);
        setSelectedPartId(null);
    }

    // part validations.
    function checkCredentialsForPart(): boolean {
        const partno = checkText(partNumber, true, setPartNumberError);
        const title = checkText(partTitle, true, setPartTitleError);

        return partno && title;
    }

    // clear message
    useEffect(() => {
        checkText(partNumber, false, setPartNumberError);
    }, [partNumber]);

    useEffect(() => {
        checkText(partTitle, false, setPartTitleError);
    }, [partTitle]);

    // handle api errors
    function showAPIErrorPart(keyName: string, error: string): void {
        if (keyName === "part_number") {
            setPartNumberError(error);
        }

        if (keyName === "part_title") {
            setPartTitleError(error);
        }
    }

    // database create part data.
    const handleCreatePart = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForPart()) return;

        setLoading(true);

        try {
            const data = await createPlanCheckPart(
                serviceId!,
                partNumber,
                partTitle
            );

            if (data.statusCode === 200) {
                planCheckTrackerDataGet();

                handleClosePartModal();
                handleResetPartFields();

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Part created successfully.',
                    life: 3000
                });
            } else if (data.statusCode === 422) {
                // database error settings
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorPart(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error creating the part.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error creating the part:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while creating the part.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const handleEditPart = (data: PlanCheckTrackerPart) => {
        setDataState('Edit');
        setShowPartModal(true);

        if (data) {
            setPartNumber(data?.part_number || '');
            setPartTitle(data.part_title || '');
            setSelectedPartId(data?.id!);
        } else {
            setSelectedPartId(null);
            setPartNumber('');
            setPartTitle('');
        }
    }

    // database upate part data.
    const handleUpdatePart = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForPart() || !selectedPartId) return;

        setLoading(true);

        try {
            const data = await updatePlanCheckPart(
                partNumber,
                partTitle,
                selectedPartId!
            );

            if (data.statusCode === 200) {
                planCheckTrackerDataGet();
                handleClosePartModal();
                handleResetPartFields();

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Part updated successfully.',
                    life: 3000
                });
            } else if (data.statusCode === 422) {
                // database error settings
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorPart(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error updating the part.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error updating the part:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating the part.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const handleDeletePart = (partId: number) => {
        confirmDialog({
            message: 'Are you sure you want to delete this part?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => deletePart(partId),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    }

    const deletePart = async (partId: number) => {
        if (!partId) return;

        try {
            const result = await deletePlanCheckPart(partId);

            if (result.statusCode === 200) {
                planCheckTrackerDataGet();
                handleClosePartModal();

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Part deleted successfully.',
                    life: 3000
                });
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the part.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error deleting the part:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while deleting the part.",
                life: 3000,
            });
        }
    }

    const handleTogglePart = (part: PlanCheckTrackerPart) => {
        setActivePartData((prevSelected) =>
            prevSelected?.id === part.id ? null : part
        );
        goToTop();
    }

    const tableActionBody = (part: PlanCheckTrackerPart) => {
        const data = part;
        const responsedItems = data?.item_list?.filter((item) => item?.building_inspector_response !== null) ?? [];

        return (
            <React.Fragment>
                <Tooltip
                    key={`edit_${part?.id}`}
                    id={`edit_${part?.id}`}
                    target={`.edit_${part?.id}`}
                />
                <Tooltip
                    key={`delete_${part?.id}`}
                    id={`delete_${part?.id}`}
                    target={`.delete_${part?.id}`}
                />

                <div className="table_action_btn_area mt-1 mb-1">
                    <button className={`table_action_btn white p-ripple`}
                        onClick={(e) => {
                            handleAddItem(part);
                            setActivePartData(part);
                            e.stopPropagation();
                        }}
                    >
                        Add Item
                        <Ripple />
                    </button>

                    <button className={`table_action_btn white p-ripple edit_${part?.id}`}
                        onClick={(e) => {
                            handleEditPart(data);
                            e.stopPropagation();
                        }}
                        data-pr-tooltip={`Edit Part`}
                        data-pr-position="top"
                        disabled={responsedItems?.length > 0}
                    >
                        Edit
                        <Ripple />
                    </button>

                    <button className={`table_action_btn white p-ripple delete_${part?.id}`}
                        onClick={(e) => {
                            handleDeletePart(data?.id!);
                            e.stopPropagation();
                        }}
                        data-pr-tooltip={`Delete Part`}
                        data-pr-position="top"
                        disabled={responsedItems?.length > 0}
                    >
                        Delete
                        <Ripple />
                    </button>
                </div>
            </React.Fragment>
        )
    };

    const planCheckTrackerItemCountBody = (part: PlanCheckTrackerPart) => {
        const data = part;
        const status = data?.status;
        const totalItemCount = data?.item_list?.length;
        const closedItemCount = data?.close_answer_count;

        return (
            <div className={`item_count_lable 
                ${totalItemCount === closedItemCount ? 'success' : ''}
                ${(totalItemCount === 0 && closedItemCount === 0) ? 'primary' : ''}
                ${status === 'Closed' ? ' success' : status === 'Open' ? ' warning' : ' primary'}`}>
                <span>
                    {String(closedItemCount).padStart(2, '0') + '/' + String(totalItemCount).padStart(2, '0')}
                </span>
            </div>
        )
    }

    const partModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} part
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleClosePartModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const partModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleClosePartModal}
            />

            <Button
                label={`${loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}`}
                onClick={dataState === 'Add' ? handleCreatePart : handleUpdatePart}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !partUpdateHasChanges}
            />
        </div>
    )
    /* *************************************************************************** */


    /* **************************** plan check tracker item ****************************** */
    const handleCloseItemModal = () => {
        setShowItemModal(false);
        handleResetItemFields();
    }

    const handleResetItemFields = () => {
        setComment('');
        setItemCodeError('');
        setCommentError('');
    }

    useEffect(() => {
        if (dataState === 'Add' && activePartData && activePartData !== null && planCheckTrackerData !== null) {
            generateItemNumber(activePartData);
        }
    }, [dataState, activePartData, planCheckTrackerData])

    const generateItemNumber = (partData: PlanCheckTrackerPart) => {
        let itemNumber = 0;

        if (partData && partData !== null) {
            const currentPartData = planCheckTrackerData?.plan_check_part?.find(part => part?.id === partData?.id)
            itemNumber = currentPartData?.item_list?.length! + 1;
        }

        setItemCode(itemNumber.toString());
    }

    const handleAddItem = (part: PlanCheckTrackerPart) => {
        if (part) {
            setSelectedPartData(part);
            setDataState('Add');
            setShowItemModal(true);
            generateItemNumber(part);
        }
    }

    // validations for part items.
    function checkCredentialsForPartItem(): boolean {
        const item = checkText(itemCode, true, setItemCodeError);
        const coment = checkText(comment, true, setCommentError);

        return item && coment;
    }

    // clear message
    useEffect(() => {
        checkText(itemCode, false, setItemCodeError);
    }, [itemCode]);

    useEffect(() => {
        checkText(comment, false, setCommentError);
    }, [comment]);

    // handle api error
    function showAPIErrorItem(keyName: string, error: string): void {
        if (keyName === "item_code") {
            setItemCodeError(error);
        }

        if (keyName === "comment") {
            setCommentError(error);
        }
    }

    const handleCreateItem = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForPartItem() || !planCheckTrackerData || !selectedPartData) return;

        setLoading(true);

        try {
            const data = await createPlanCheckItem(
                planCheckTrackerData?.id!,
                selectedPartData !== null ? "Detail" : "Basic",
                serviceId!,
                selectedPartData !== null ? selectedPartData?.id! : null,
                itemCode,
                comment
            );

            if (data.statusCode === 200) {
                planCheckTrackerDataGet();
                handleResetItemFields();
                generateItemNumber(activePartData!);

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Item added successfully.',
                    life: 3000
                });
            } else if (data.statusCode === 422) {
                // database error
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorItem(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error creating the item.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error creating the item:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while creating the item.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const handleEditItem = (item: PlanCheckTrackerItemList, part: PlanCheckTrackerPart) => {
        if (part) {
            setSelectedPartData(part);

            if (item) {
                setDataState('Edit');
                setShowItemModal(true);

                setItemCode(item?.item_code || '')
                setComment(item?.comment || '')
                setSelectedItemData(item);
            } else {
                setSelectedItemData(null);
                setItemCode('');
                setComment('');
            }
        }
    }

    const handleUpdateItem = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForPartItem() || !planCheckTrackerData || !selectedItemData) return;

        setLoading(true);

        try {
            const data = await updatePlanCheckItem(
                planCheckTrackerData?.id!,
                itemCode,
                comment,
                "Open",
                selectedItemData?.id!
            );

            if (data.statusCode === 200) {
                planCheckTrackerDataGet();
                handleCloseItemModal();
                handleResetItemFields();

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Item updated successfully.',
                    life: 3000
                });
            } else if (data.statusCode === 422) {
                // database error
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorItem(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error updating the item.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error updating the item:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating the item.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const handleDeleteItem = (itemId: number) => {
        confirmDialog({
            message: `Are you sure you want to delete this item?`,
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => deleteItem(itemId),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    }

    const deleteItem = async (itemId: number) => {
        if (!itemId) return;

        setDeleteLoading(true);

        try {
            const result = await deletePlanCheckItem(itemId);

            if (result.statusCode === 200) {
                planCheckTrackerDataGet();
                handleCloseItemModal();

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Item deleted successfully.',
                    life: 3000
                });
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the part.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error error deleting the part:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while error deleting the part.",
                life: 3000,
            });
        } finally {
            setDeleteLoading(false);
        }
    }

    const itemModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} item detail - <span className="custom">{activePartData?.part_number}</span>
            </h5>

            {dataState === "Edit" ? (
                <React.Fragment>
                    {deleteLoading ? (
                        <TableActionLoader className="is_modal_loader" />
                    ) : (
                        <button
                            type="button"
                            className="close_modal_btn p-ripple"
                            onClick={() => handleDeleteItem(selectedItemData?.id!)}>
                            <i className="bi bi-trash3"></i>
                            <Ripple />
                        </button>
                    )}
                </React.Fragment>
            ) : (
                <button
                    type="button"
                    className="close_modal_btn p-ripple"
                    onClick={handleCloseItemModal}>
                    <i className="bi bi-x-circle"></i>
                    <Ripple />
                </button>
            )}
        </div>
    )

    const itemModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseItemModal}
            />

            <Button
                label={`${loading ? 'Processing' : dataState === 'Add' ? 'Save & Next' : 'Update'}`}
                onClick={dataState === 'Add' ? handleCreateItem : handleUpdateItem}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !itemUpdateHasChanges}
            />
        </div>
    )
    /* *************************************************************************** */

    const handleNavigateReport = () => {
        navigate(
            `/project/detail/default-service/pre-commencement/plan-check-tracker/report`,
            { state: { projectData: projectData, planCheckTrackerData: planCheckTrackerData } }
        );
    }

    /* **************************** For architect reply ************************ */
    useEffect(() => {
        if (planCheckTrackerData && planCheckTrackerData !== null) {
            if (dataState === 'Edit' && selectedItemData && selectedItemData !== null && activePartData && activePartData !== null) {
                const currentPart = planCheckTrackerData?.plan_check_part?.find(part => part?.id === activePartData?.id);

                if (currentPart !== null) {
                    const updatedItemData = currentPart?.item_list?.find(item => item?.id === selectedItemData?.id);
                    setSelectedItemData(updatedItemData || null);
                }
            }
        }

    }, [dataState, selectedItemData, activePartData, planCheckTrackerData]);

    useEffect(() => {
        if (selectedItemData && selectedItemData !== null) {
            if (arcResponseNote !== selectedItemData?.architect_response) {
                setArcResponseUpdateHasChanges(true);
            } else {
                setArcResponseUpdateHasChanges(false);
            }
        }
    }, [selectedItemData, arcResponseNote]);

    const getNextPendingItem = (
        currentPartId: number,
        currentItemId: number,
        by: 'ARC' | 'BI'
    ): { part: PlanCheckTrackerPart; item: PlanCheckTrackerItemList } | null => {

        if (!planCheckTrackerData?.plan_check_part) return null;

        const partsWithItems: PlanCheckTrackerPart[] = planCheckTrackerData?.plan_check_part?.filter(
            (part): part is PlanCheckTrackerPart =>
                Array.isArray(part.item_list) && part.item_list?.length > 0
        );

        const currentPartIndex: number = partsWithItems?.findIndex(
            (part) => part.id === currentPartId
        );

        if (currentPartIndex === -1) return null;

        const isPending = (item: PlanCheckTrackerItemList) =>
            by === 'ARC'
                ? item.architect_response === null
                : item.building_inspector_response === null;

        // Check next items in the SAME part
        const currentPart = partsWithItems[currentPartIndex];
        const itemList = currentPart?.item_list!;

        const currentItemIndex = itemList.findIndex(
            (item) => item?.id === currentItemId
        );

        if (currentItemIndex !== -1) {
            for (let i = currentItemIndex + 1; i < itemList.length; i++) {
                const item = itemList[i];

                if (isPending(item)) {
                    return { part: currentPart, item };
                }
            }
        }

        // Move to NEXT parts
        for (let p = currentPartIndex + 1; p < partsWithItems.length; p++) {
            const part = partsWithItems[p];
            const items = part.item_list!;

            for (const item of items) {
                if (isPending(item)) {
                    return { part, item };
                }
            }
        }

        // No pending items left
        return null;
    };

    const handleCloseArcReplyModal = (mode: 'Next' | 'Close') => {
        if (mode === 'Close') {
            setShowArcReplyModal(false);
            setSelectedItemData(null);
        }

        handleResetArcReplyFields();
        handleResetArcReplyErrors();
    }

    const handleResetArcReplyFields = () => {
        setArcResponseNote('');
        setArcResponseFiles([]);
    }

    const handleResetArcReplyErrors = () => {
        setArcResponseNoteError('');
        setArcResponseFileError('');
    }

    const handleAddArcReply = (item: PlanCheckTrackerItemList, part: PlanCheckTrackerPart) => {
        if (part) {
            setSelectedPartData(part);

            if (item) {
                setDataState('Add');
                setShowArcReplyModal(true);
                setSelectedItemData(item);

                handleResetArcReplyFields();
                handleResetArcReplyErrors();
            }
        }
    }

    // Response validations.
    function checkCredentialsForArcReply(): boolean {
        const item = checkText(arcResponseNote, true, setArcResponseNoteError);

        return item;
    }

    // clear message
    useEffect(() => {
        checkText(arcResponseNote, false, setArcResponseNoteError);
    }, [arcResponseNote]);

    // handle api error
    function showAPIErrorArcReply(keyName: string, error: string): void {
        if (keyName === "response") {
            setArcResponseNoteError(error);
        }
    }

    const handleCreateReplyByArc = async (e: React.FormEvent, replyState: 'Next' | 'Close') => {
        e.preventDefault();

        if (
            !checkCredentialsForArcReply() ||
            !selectedItemData ||
            !selectedPartData ||
            !planCheckTrackerData
        ) {
            return;
        }

        replyState === 'Next'
            ? setSaveNextLoading(true)
            : setLoading(true);

        try {
            const data = await responsePlanCheck(
                planCheckTrackerData?.id!,
                selectedItemData?.id!,
                arcResponseNote,
                "Architect",
                null
            );

            // files uploading
            if (arcResponseFiles && arcResponseFiles.length > 0) {
                const upload = await uploadPlanCheckFiles(
                    planCheckTrackerData?.id!,
                    selectedItemData?.id!,
                    arcResponseFiles
                );

                if (upload.statusCode !== 200) {
                    toast?.current?.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error Uploading files.",
                        life: 3000,
                    });
                }
            }

            if (data.statusCode === 200) {
                planCheckTrackerDataGet();

                if (replyState === 'Close') {
                    handleCloseArcReplyModal('Close');
                } else {
                    if (!selectedPartData || !selectedItemData) {
                        handleCloseArcReplyModal('Close');
                        return;
                    }

                    const nextData = getNextPendingItem(
                        selectedPartData.id,
                        selectedItemData.id,
                        'ARC'
                    );

                    if (nextData) {
                        handleCloseArcReplyModal('Next');

                        setSelectedPartData(nextData.part);
                        setActivePartData(nextData.part);
                        setSelectedItemData(nextData.item);
                    } else {
                        handleCloseArcReplyModal('Close');
                    }
                }

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "The response was successfully submitted.",
                    life: 2000,
                });
            } else if (data.statusCode === 422) {
                // database error
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorArcReply(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error submitting the response.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error submitting the response:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while submitting the response.",
                life: 3000,
            });
        } finally {
            replyState === 'Next'
                ? setSaveNextLoading(false)
                : setLoading(false);
        }
    }

    const handleEditArcResponse = (item: PlanCheckTrackerItemList, part: PlanCheckTrackerPart) => {
        if (part) {
            setSelectedPartData(part);

            if (item) {
                setDataState('Edit');
                setShowArcReplyModal(true);
                setSelectedItemData(item);

                setArcResponseNote(item?.architect_response!);
            }
        }
    }

    const handleUpdateReplyByArc = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForArcReply() || !selectedItemData) return;

        setLoading(true);

        try {
            const data = await responsePlanCheck(
                planCheckTrackerData?.id!,
                selectedItemData?.id!,
                arcResponseNote,
                "Architect",
                null
            );

            // files uploading
            if (arcResponseFiles && arcResponseFiles.length > 0) {
                const upload = await uploadPlanCheckFiles(
                    planCheckTrackerData?.id!,
                    selectedItemData?.id!,
                    arcResponseFiles
                );

                if (upload.statusCode !== 200) {
                    toast?.current?.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error Uploading files.",
                        life: 3000,
                    });
                }
            }

            if (data.statusCode === 200) {
                planCheckTrackerDataGet();
                handleCloseArcReplyModal('Close');

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "The response was successfully updated.",
                    life: 2000,
                });
            } else if (data.statusCode === 422) {
                // database error
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorArcReply(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error updating the response.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error updating the response:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating the response.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const arcReplyModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} reply
                <small className="modal_sub_title">
                    {selectedPartData?.part_number}&nbsp;-&nbsp;{selectedPartData?.part_title ?? '---'}
                </small>
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={() => handleCloseArcReplyModal('Close')}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const arcReplyModalFooter = () => {
        const hasMoreData = (() => {
            if (
                !planCheckTrackerData ||
                !selectedPartData ||
                !selectedItemData ||
                !selectedPartData.id ||
                !selectedItemData.id
            ) {
                return false;
            }

            return !!getNextPendingItem(
                selectedPartData.id,
                selectedItemData.id,
                'ARC'
            );
        })();

        return (
            <div className="custom_modal_footer">
                <Button
                    label="Close"
                    className="custom_btn secondary"
                    onClick={() => handleCloseArcReplyModal('Close')}
                />

                {dataState === 'Add' ? (
                    <>
                        <Button
                            label={loading ? 'Processing' : 'Save & Close'}
                            onClick={(e) => handleCreateReplyByArc(e, 'Close')}
                            loading={loading}
                            className={`custom_btn ${hasMoreData ? 'primary_light' : 'primary'}`}
                        />

                        {hasMoreData && (
                            <Button
                                label={saveNextLoading ? 'Processing' : 'Save & Next'}
                                onClick={(e) => handleCreateReplyByArc(e, 'Next')}
                                loading={saveNextLoading}
                                className="custom_btn primary"
                            />
                        )}
                    </>
                ) : (
                    <Button
                        label={loading ? 'Processing' : 'Update'}
                        onClick={handleUpdateReplyByArc}
                        loading={loading}
                        className="custom_btn primary"
                        disabled={dataState === 'Edit' && arcResponseFiles?.length === 0 && !arcResponseUpdateHasChanges}
                    />
                )}
            </div>
        )
    }

    const handleDeleteResponseFile = (fileId: number) => {
        confirmDialog({
            message: "Are you sure you want to delete this file?",
            header: "Confirm the deletion",
            icon: "bi bi-info-circle",
            defaultFocus: "accept",
            acceptClassName: "p-button-danger",
            accept: () => deleteResponseFile(fileId),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    };

    const deleteResponseFile = async (fileId: number) => {
        if (!fileId) return;

        setResponseFileDeleteLoadingId(fileId);

        try {
            const result = await deletePlanCheckFile(fileId);

            if (result.statusCode === 200) {
                planCheckTrackerDataGet();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "File deleted successfully.",
                    life: 2000,
                });
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error deleting the file.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error deleting the file:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while deleting the file.",
                life: 3000,
            });
        } finally {
            setResponseFileDeleteLoadingId(null);
        }
    };

    const handleUploadResponseFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const filesArray = Array.from(event.target.files);
            setArcResponseFiles((prev) => [...prev, ...filesArray]);
        }
    };

    const handleRemoveResponseFile = (fileIndex: number) => {
        setArcResponseFiles((prev) => prev.filter((_, i) => i !== fileIndex));
    };
    /* *************************************************************************** */


    /* ********************** For building inspector reply ******************** */
    useEffect(() => {
        if (selectedItemData && selectedItemData !== null) {
            const mappedStatus = selectedItemData?.status === "Closed" ? "Satisfactory" : "Unsatisfactory";

            if (biResponseNote !== null && biResponseNote !== selectedItemData?.building_inspector_response) {
                setBiResponseUpdateHasChanges(true);
            } else if (biResponseStatus !== mappedStatus) {
                setBiResponseUpdateHasChanges(true);
            }
            else {
                setBiResponseUpdateHasChanges(false);
            }
        }
    }, [selectedItemData, biResponseNote, biResponseStatus]);

    const handleCloseBiReplyModal = (mode: 'Next' | 'Close') => {
        if (mode === 'Close') {
            setShowBiReplyModal(false);
            setSelectedItemData(null);
        }

        handleResetBiReplyFields();
        handleResetBiReplyErrors();
    }

    const handleResetBiReplyFields = () => {
        setBiResponseNote('');
        setBiResponseStatus('Unsatisfactory');
    }

    const handleResetBiReplyErrors = () => {
        setBiResponseNoteError('');
        setBiResponseStatusError('');
    }

    const handleAddBiReply = (item: PlanCheckTrackerItemList, part: PlanCheckTrackerPart) => {
        if (part) {
            setSelectedPartData(part);

            if (item) {
                setDataState('Add');
                setShowBiReplyModal(true);
                setSelectedItemData(item);

                handleResetBiReplyFields();
                handleResetBiReplyErrors();
            }
        }
    }

    // Response validations.
    function checkCredentialsForBiReply(): boolean {
        const item = checkText(biResponseNote, false, setBiResponseNoteError);

        return item;
    }

    // clear message
    useEffect(() => {
        checkText(biResponseNote, false, setBiResponseNoteError);
    }, [biResponseNote]);

    // handle api error
    function showAPIErrorBiReply(keyName: string, error: string): void {
        if (keyName === "response") {
            setBiResponseNoteError(error);
        }
    }

    const handleCreateReplyByBi = async (e: React.FormEvent, replyState: 'Next' | 'Close') => {
        e.preventDefault();

        if (!checkCredentialsForBiReply() || selectedItemData === null) return;

        replyState === 'Next'
            ? setSaveNextLoading(true)
            : setLoading(true);

        try {
            const data = await responsePlanCheck(
                planCheckTrackerData?.id!,
                selectedItemData?.id!,
                biResponseNote,
                "Building Inspector",
                biResponseStatus
            );

            if (data.statusCode === 200) {
                planCheckTrackerDataGet();

                if (replyState === 'Close') {
                    handleCloseBiReplyModal('Close');
                } else {
                    if (!selectedPartData || !selectedItemData) {
                        handleCloseBiReplyModal('Close');
                        return;
                    }

                    const nextData = getNextPendingItem(
                        selectedPartData.id,
                        selectedItemData.id,
                        'BI'
                    );

                    if (nextData) {
                        handleCloseBiReplyModal('Next');

                        setSelectedPartData(nextData.part);
                        setActivePartData(nextData.part);
                        setSelectedItemData(nextData.item);
                    } else {
                        handleCloseBiReplyModal('Close');
                    }
                }

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "The response was successfully submitted.",
                    life: 2000,
                });
            } else if (data.statusCode === 422) {
                // database error
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorBiReply(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error submitting the response.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error submitting the response:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while submitting the response.",
                life: 3000,
            });
        } finally {
            replyState === 'Next'
                ? setSaveNextLoading(false)
                : setLoading(false);
        }
    }

    const handleEditBiReply = (item: PlanCheckTrackerItemList, part: PlanCheckTrackerPart) => {
        if (part) {
            setSelectedPartData(part);

            if (item) {
                setDataState('Edit');
                setShowBiReplyModal(true);
                setSelectedItemData(item);

                setBiResponseNote(item?.building_inspector_response === 'N/A' ? '' : item?.building_inspector_response!);
                setBiResponseStatus(item?.status && item?.status === 'Open' ? 'Unsatisfactory' : 'Satisfactory');
            }
        }
    }

    const handleUpdateReplyByBi = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForBiReply() || selectedItemData === null) return;

        setLoading(true);

        try {
            const data = await responsePlanCheck(
                planCheckTrackerData?.id!,
                selectedItemData?.id!,
                biResponseNote,
                "Building Inspector",
                biResponseStatus
            );

            if (data.statusCode === 200) {
                planCheckTrackerDataGet();
                handleCloseBiReplyModal('Close');

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "The response was successfully updated.",
                    life: 2000,
                });
            } else if (data.statusCode === 422) {
                // database error
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorBiReply(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error updating the response.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error updating the response:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating the response.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const biReplyModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} reply
                <small className="modal_sub_title">
                    {selectedPartData?.part_number}&nbsp;-&nbsp;{selectedPartData?.part_title ?? '---'}
                </small>
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={() => handleCloseBiReplyModal('Close')}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const biReplyModalFooter = () => {
        const hasMoreData = (() => {
            if (
                !planCheckTrackerData ||
                !selectedPartData ||
                !selectedItemData ||
                !selectedPartData.id ||
                !selectedItemData.id
            ) {
                return false;
            }

            return !!getNextPendingItem(
                selectedPartData.id,
                selectedItemData.id,
                'BI'
            );
        })();

        return (
            <div className="custom_modal_footer">
                <Button
                    label="Close"
                    className="custom_btn secondary"
                    onClick={() => handleCloseBiReplyModal('Close')}
                />

                {dataState === 'Add' ? (
                    <>
                        <Button
                            label={loading ? 'Processing' : 'Reply & Close'}
                            onClick={(e) => handleCreateReplyByBi(e, 'Close')}
                            loading={loading}
                            className={`custom_btn ${hasMoreData ? 'primary_light' : 'primary'}`}
                        />

                        {hasMoreData && (
                            <Button
                                label={`${saveNextLoading ? 'Processing' : 'Reply & Next'}`}
                                onClick={(e) => handleCreateReplyByBi(e, 'Next')}
                                loading={saveNextLoading}
                                className="custom_btn primary"
                            />
                        )}
                    </>
                ) : (
                    <Button
                        label={loading ? 'Processing' : 'Update'}
                        onClick={handleUpdateReplyByBi}
                        loading={loading}
                        className="custom_btn primary"
                        disabled={dataState === 'Edit' && !biResponseUpdateHasChanges}
                    />
                )}
            </div>
        )
    }
    /* *************************************************************************** */


    const handleCloseFileViewModal = () => {
        setShowItemDocViewModal(false);
    };

    const handleViewDocument = (filePath: string) => {
        if (filePath && filePath !== "") {
            setShowItemDocViewModal(true);
            setDocumentFilePath(filePath);
        } else {
            setDocumentFilePath("");
        }
    };


    /* ******************************** Download ********************************* */
    const handleDownloadReport = async () => {
        if (isDownloading) return;
        // const reportPath = planCheckTrackerData?.file_download_path;
        const reportPath = planCheckTrackerData?.file_download_path;
        if (!reportPath) {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "File not found!",
                    life: 3000,
                });
            }
            return;
        }

        setIsDownloading(true);

        setTimeout(async () => {
            try {
                const response = await fetch(reportPath);

                if (!response.ok) {
                    throw new Error("Failed to fetch the file.");
                }

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = reportPath.split("/").pop() || "plan check tracker report.pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "File downloaded successfully.",
                        life: 3000,
                    });
                }
            } catch (error) {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "Error downloading the file",
                        life: 3000,
                    });
                }
            } finally {
                setIsDownloading(false);
            }
        }, 500);
    };

    const handleViewReportDocument = () => {
        if (planCheckTrackerData?.file_download_path) {
            setShowItemDocViewModal(true);
            setDocumentFilePath(planCheckTrackerData?.file_download_path);
        }
    };
    /* *************************************************************************** */


    /* *********************************** Mail ********************************** */
    const handleCloseMailModal = () => {
        setShowMailModal(false);
        setEmailAddress("");
    };

    const handleAddMail = () => {
        setShowMailModal(true);
    };

    // need to add email for send report.
    // validations.
    function checkCredentialsEmail(): boolean {
        const mail = checkEmail(emailAddress, true, setEmailAddressError);
        return mail;
    }

    // clear message
    useEffect(() => {
        checkEmail(emailAddress, false, setEmailAddressError);
    }, [emailAddress]);

    // database error set message.
    function showAPIErrorEmail(keyName: string, error: string): void {
        if (keyName === "email") {
            setEmailAddressError(error);
        }
    }

    // send mail database functions.
    const handleSendMail = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsEmail() || planCheckTrackerData === null) return;

        setLoading(true);

        try {
            const data = await sharePlanCheck(planCheckTrackerData?.id!, emailAddress);

            if (data.statusCode === 200) {
                planCheckTrackerDataGet();
                handleCloseMailModal();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Email sent successfully.",
                    life: 3000,
                });
            } else if (data.statusCode === 422) {
                // database error
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorEmail(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error sending the email.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error sending the email:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while sending the email.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    const mailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Send report via mail</h5>
            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseMailModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const mailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseMailModal}
            />
            <Button
                loading={loading}
                label={`${loading ? "Sending" : "Send"}`}
                className="custom_btn primary"
                onClick={handleSendMail}
            />
        </div>
    );
    /* *************************************************************************** */


    /* ********************** Response create by building inspector *********************** */
    const handleCreateResponseByBi = () => {
        confirmDialog({
            message: 'Are you sure, Do you want to create a new document to respond to the plan check tracker?',
            header: 'Confirm the Publishion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => createBiResponse(),
            resizable: false,
            draggable: false,
            dismissableMask: true
        });
    }

    const createBiResponse = async () => {
        if (!planCheckTrackerData) return;

        try {
            const response = await createPlanCheckTrackerResponseByBi(planCheckTrackerData?.id!);

            if (response.statusCode === 200) {
                await planCheckTrackerDataGet();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "The response was successfully created.",
                    life: 3000,
                });

                const navigateToList = () => {
                    navigate(
                        '/project/detail/default-service/pre-commencement/plan-check-tracker/list',
                        { state: { data: projectData } }
                    );
                };

                if (response?.data && plancheckTrackerList?.length > 0) {
                    const newPlanCheckTracker = response?.data;

                    if (newPlanCheckTracker) {
                        setTimeout(() => {
                            navigate(location.pathname, {
                                replace: true,
                                state: {
                                    data: newPlanCheckTracker,
                                    projectData: projectData,
                                    fromBiResponse: true,
                                },
                            });
                        }, 500);
                    } else {
                        navigateToList();
                    }
                } else {
                    navigateToList();
                }
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "An error occurred while creating the response.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error creating the response:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while creating the response.",
                life: 3000,
            });
        }
    }
    /* *************************************************************************** */

    /* ********************** Response create by architect *********************** */
    const handleCreateResponseByArc = () => {
        confirmDialog({
            message: 'Are you sure, Do you want to create a new document to respond to the plan check tracker?',
            header: 'Confirm the Publishion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => createArcResponse(),
            resizable: false,
            draggable: false,
            dismissableMask: true
        });
    }

    const createArcResponse = async () => {
        if (!planCheckTrackerData) return;

        try {
            const response = await createPlanCheckTrackerResponseByArc(planCheckTrackerData?.id);

            if (response.statusCode === 200) {
                await planCheckTrackerDataGet();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "The response was successfully created.",
                    life: 3000,
                });

                const navigateToList = () => {
                    navigate(
                        '/project/detail/default-service/pre-commencement/plan-check-tracker/list',
                        { state: { data: projectData } }
                    );
                };

                if (response?.data && plancheckTrackerList?.length > 0) {
                    const newPlanCheck = response?.data;

                    if (newPlanCheck) {
                        setTimeout(() => {
                            navigate(location.pathname, {
                                replace: true,
                                state: {
                                    data: newPlanCheck,
                                    projectData: projectData,
                                    fromArcResponse: true,
                                },
                            });
                        }, 500);
                    } else {
                        navigateToList();
                    }
                } else {
                    navigateToList();
                }
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "An error occurred while creating the response.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error creating the response:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while creating the response.",
                life: 3000,
            });
        }
    }
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content full_screen_view">
                {dataLoading && initialDataLoading ? (
                    <div className="loading_container">
                        <ProgressSpinner className="progresscircle custom" style={{ width: '50px', height: '50px', stroke: '#8953D7' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration="1s" />
                    </div>
                ) : error ? (
                    <ErrorPage errorMessage={error} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area with_responsive">
                            <ul className="page_breadcrumb">
                                {isInspector && (
                                    <li className="page_breadcrumb_item has_child has_link"
                                        onClick={() => navigate('/project')}
                                    >
                                        Shared folders
                                    </li>
                                )}

                                <li
                                    className={`page_breadcrumb_item has_child ${isArchitect ? 'has_link' : ''}`}
                                    onClick={() => isArchitect ? navigate('/project/detail', { state: { projectData: projectData } }) : null}
                                >
                                    {projectData.job_number} : {projectData.address}
                                </li>

                                <li className="page_breadcrumb_item has_child">
                                    {projectData?.folder}
                                </li>

                                <li
                                    className="page_breadcrumb_item has_child has_link"
                                    onClick={() => navigate(-2)}>
                                    {projectData?.feature_name}
                                </li>

                                <li
                                    className="page_breadcrumb_item has_child has_link"
                                    onClick={() => navigate(-1)}>
                                    {projectData?.feature_name} list
                                </li>

                                <li className="page_breadcrumb_item active">
                                    {projectData?.feature_name} - Detail
                                </li>
                            </ul>

                            <GoBackButton />
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3 justify-content-end">
                            <div className="page_action_area">
                                {hasPermissionAddSignature && (
                                    (isInspector && isSignedAndPublishedByBi === false && !isResponseCreated && planCheckTrackerData?.stage_for === 'Response') ||
                                    (isArchitect && isSignedAndPublishedByArc === false && !isResponseCreated && planCheckTrackerData?.stage_for === 'Original')
                                ) && (
                                        <button
                                            className="add_data_link_btn p-ripple"
                                            onClick={handleNavigateReport}
                                        >
                                            <i className="bi bi-send"></i>
                                            <span>Sign & Publish</span>
                                            <i className="bi bi-box-arrow-up-right"></i>
                                            <Ripple />
                                        </button>
                                    )
                                }

                                {planCheckTrackerData?.file_download_path !== null && (
                                    <React.Fragment>
                                        {hasPermissionDownloadFile && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleDownloadReport}
                                                disabled={isDownloading}
                                            >
                                                {isDownloading ? (
                                                    <ProgressSpinner
                                                        style={{ width: "20px", height: "20px" }}
                                                        strokeWidth="5"
                                                        fill="none"
                                                        animationDuration="1s"
                                                        className="spin_loader"
                                                    />
                                                ) : (
                                                    <i className="bi bi-download"></i>
                                                )}
                                                <span>{isDownloading ? "Downloading" : "Download"}</span>
                                                <Ripple />
                                            </button>
                                        )}

                                        {isInspector && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleAddMail}
                                            >
                                                <i className="bi bi-envelope"></i>
                                                <span>Send</span>
                                                <Ripple />
                                            </button>
                                        )}
                                    </React.Fragment>
                                )}

                                {isInspector && (
                                    !isResponseCreated && planCheckTrackerData?.stage_for === 'Original' && !isAllSatisfied && (
                                        <button
                                            className="add_data_link_btn p-ripple"
                                            onClick={handleCreateResponseByBi}
                                        >
                                            <i className="bi bi-reply"></i>
                                            <span>Response</span>
                                            <Ripple />
                                        </button>
                                    )
                                )}

                                {isArchitect && (
                                    !isResponseCreated && planCheckTrackerData?.stage_for === 'Response' && !isAllSatisfied && (
                                        <button
                                            className="add_data_link_btn p-ripple"
                                            onClick={handleCreateResponseByArc}
                                        >
                                            <i className="bi bi-reply"></i>
                                            <span>Response</span>
                                            <Ripple />
                                        </button>
                                    )
                                )}

                                <button
                                    className="add_data_link_btn p-ripple"
                                    onClick={handleNavigateReport}>
                                    <i className="bi bi-eye"></i>
                                    <span>Preview</span>
                                    <Ripple />
                                </button>

                                {isSignedAndPublishedByBi === false && isSignedAndPublishedByArc === false && isArchitect && (
                                    <button
                                        className="add_data_link_btn p-ripple"
                                        onClick={handleAddPart}
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add part</span>
                                        <Ripple />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="page_main_content_section no_header">
                            {planCheckTrackerPartData && planCheckTrackerPartData?.length > 0 ? (
                                <div className="pck_track_list_area">
                                    {dataLoading && (
                                        <CustomTableLoader />
                                    )}

                                    <div className="table-responsive pck_track_list_table_area">
                                        <table className="table pck_track_list_table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '4%' }} className="text_no_wrap"></th>

                                                    <th style={{ width: '16%' }} className="text_no_wrap">Part no.</th>

                                                    <th style={{ width: (isSignedAndPublishedByBi === false && isSignedAndPublishedByArc === false && isArchitect) ? "45%" : "70%" }}
                                                        className="text_no_wrap">
                                                        Part title
                                                    </th>
                                                    <th style={{ width: '10%' }}></th>

                                                    {(isSignedAndPublishedByBi === false && isSignedAndPublishedByArc === false && isArchitect) && (
                                                        <th style={{ width: "25%" }}></th>
                                                    )}
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {planCheckTrackerPartData?.map((part) => {
                                                    const isActive = part?.id === activePartData?.id;
                                                    return (
                                                        <React.Fragment>
                                                            <tr
                                                                key={`part_${part?.id}`}
                                                                onClick={() => handleTogglePart(part)}
                                                                className={`part_row ${isActive ? 'active' : ''}`}
                                                            >
                                                                <td
                                                                    style={{ width: "3rem" }}
                                                                    className="text-center">
                                                                    <i className={`bi bi-chevron-right ${isActive ? 'toggled' : ''} data_toggle_icon`}></i>
                                                                </td>

                                                                <td style={{ width: '16%' }} className="text_no_wrap">
                                                                    {part?.part_number}
                                                                </td>

                                                                <td
                                                                    style={{ width: (isSignedAndPublishedByBi === false && isSignedAndPublishedByArc === false && isArchitect) ? "45%" : "70%" }}
                                                                    className="note_column"
                                                                >
                                                                    {part?.part_title}
                                                                </td>

                                                                <td style={{ width: '10%' }}>
                                                                    {planCheckTrackerItemCountBody(part)}
                                                                </td>

                                                                {isSignedAndPublishedByBi === false && isSignedAndPublishedByArc === false && isArchitect && (
                                                                    <td style={{ width: "25%" }}>
                                                                        {tableActionBody(part)}
                                                                    </td>
                                                                )}
                                                            </tr>

                                                            <AnimatePresence initial={false}>
                                                                {isActive && (
                                                                    <tr className="part_item_row">
                                                                        <td colSpan={5} style={{ width: '100%' }} className='p-0'>
                                                                            <motion.div
                                                                                initial={{ height: 0, opacity: 0 }}
                                                                                animate={{ height: 'auto', opacity: 1 }}
                                                                                exit={{ height: 0, opacity: 0 }}
                                                                                transition={{ duration: 0.3, ease: [0.3, 0, 0.2, 1] }}
                                                                                style={{ overflow: 'hidden' }}
                                                                            >
                                                                                <div className="item_list_wrapper">
                                                                                    {part?.item_list && part?.item_list?.length > 0 ? (
                                                                                        part.item_list.map((item) => (
                                                                                            <div key={`item_${item?.id}`} className={`pck_track_list_item_area with_indent ${item?.status === 'Open' ? 'warning' : 'success'}`}>
                                                                                                <div className="pck_track_list_item_header">
                                                                                                    <span className="item_code">{item?.item_code}.</span>
                                                                                                    <span className="item_cmt">{item?.comment}</span>
                                                                                                </div>

                                                                                                <div className="response_display_area custom_padding">
                                                                                                    {/* Showing ARC response for BI */}
                                                                                                    {isInspector && (
                                                                                                        <React.Fragment>
                                                                                                            {item?.architect_response && item?.architect_response !== null && (
                                                                                                                <div className="response_input_area mb-md-0 readonly">
                                                                                                                    <label>Architect Response :&nbsp;</label>

                                                                                                                    <p className="first_letter_capitalized">
                                                                                                                        {item?.architect_response ? (
                                                                                                                            item?.architect_response
                                                                                                                        ) : (
                                                                                                                            <span className="text_empty text_italic">N/A</span>
                                                                                                                        )}
                                                                                                                    </p>

                                                                                                                    {item?.file_list && item?.file_list?.length > 0 && (
                                                                                                                        <div className="response_file_area mt-3 pb-0">
                                                                                                                            <div className="checklist_img_container">
                                                                                                                                {item?.file_list?.map((file, index) => (
                                                                                                                                    <div
                                                                                                                                        className="note_image_container response_image_container"
                                                                                                                                        key={file?.id}
                                                                                                                                    >
                                                                                                                                        {file?.extension === "pdf" ? (
                                                                                                                                            <div
                                                                                                                                                className="response_doc_file_container"
                                                                                                                                                onClick={() => handleViewDocument(file?.path!)}
                                                                                                                                            >
                                                                                                                                                <i className="bi bi-file-earmark-pdf"></i>
                                                                                                                                            </div>
                                                                                                                                        ) : (
                                                                                                                                            <ImagePreview
                                                                                                                                                src={file?.path!}
                                                                                                                                                alt={`arc_response_img_for_bi${index + 1}`}
                                                                                                                                            />
                                                                                                                                        )}
                                                                                                                                    </div>
                                                                                                                                ))}
                                                                                                                            </div>
                                                                                                                        </div>
                                                                                                                    )}
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </React.Fragment>
                                                                                                    )}

                                                                                                    {/* Showing BI response for ARC */}
                                                                                                    {isArchitect && (
                                                                                                        <React.Fragment>
                                                                                                            {item?.building_inspector_response && item?.building_inspector_response !== null && (
                                                                                                                <div className="response_input_area readonly mb-3">
                                                                                                                    <label className="text-danger">Building Inspector Response :&nbsp;</label>

                                                                                                                    <p className={`first_letter_capitalized ${(item?.building_inspector_response === 'N/A' && item?.status === 'Open') ? 'text_empty text_italic' : ''}`}>
                                                                                                                        {item?.status === 'Closed' && item?.building_inspector_response === 'N/A' ? 'Satisfactory' :
                                                                                                                            item?.building_inspector_response ? (
                                                                                                                                item?.building_inspector_response
                                                                                                                            ) : (
                                                                                                                                <span className="text_empty text_italic">N/A</span>
                                                                                                                            )
                                                                                                                        }
                                                                                                                    </p>
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </React.Fragment>
                                                                                                    )}

                                                                                                    {/* Showing ARC response for ARC */}
                                                                                                    {isArchitect && (
                                                                                                        <React.Fragment>
                                                                                                            {item?.architect_response && item?.architect_response !== null && isSignedAndPublishedByBi === false && (
                                                                                                                <div className="response_input_area mb-md-0 readonly">
                                                                                                                    <label>My Response :&nbsp;</label>

                                                                                                                    <p className="first_letter_capitalized">
                                                                                                                        {item?.architect_response ? (
                                                                                                                            item?.architect_response
                                                                                                                        ) : (
                                                                                                                            <span className="text_empty text_italic">N/A</span>
                                                                                                                        )}
                                                                                                                    </p>

                                                                                                                    {item?.file_list && item?.file_list?.length > 0 && (
                                                                                                                        <div className="response_file_area mt-3 pb-0">
                                                                                                                            <div className="checklist_img_container">
                                                                                                                                {item?.file_list?.map((file, index) => (
                                                                                                                                    <div
                                                                                                                                        className="note_image_container response_image_container"
                                                                                                                                        key={file?.id}
                                                                                                                                    >
                                                                                                                                        {file?.extension === "pdf" ? (
                                                                                                                                            <div
                                                                                                                                                className="response_doc_file_container"
                                                                                                                                                onClick={() => handleViewDocument(file?.path!)}
                                                                                                                                            >
                                                                                                                                                <i className="bi bi-file-earmark-pdf"></i>
                                                                                                                                            </div>
                                                                                                                                        ) : (
                                                                                                                                            <ImagePreview
                                                                                                                                                src={file?.path!}
                                                                                                                                                alt={`arc_response_img_for_arc${index + 1}`}
                                                                                                                                            />
                                                                                                                                        )}
                                                                                                                                    </div>
                                                                                                                                ))}
                                                                                                                            </div>
                                                                                                                        </div>
                                                                                                                    )}
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </React.Fragment>
                                                                                                    )}

                                                                                                    {/* Showing BI response for BI */}
                                                                                                    {isInspector && (
                                                                                                        <React.Fragment>
                                                                                                            {item?.building_inspector_response && item?.building_inspector_response !== null && planCheckTrackerData?.stage_for === 'Response' && (
                                                                                                                <div className="response_input_area readonly mb-3">
                                                                                                                    <label className="text-danger">My Response :&nbsp;</label>

                                                                                                                    <p className={`first_letter_capitalized ${(item?.building_inspector_response === 'N/A' && item?.status === 'Open') ? 'text_empty text_italic' : ''}`}>
                                                                                                                        {item?.status === 'Closed' && item?.building_inspector_response === 'N/A' ? 'Satisfactory' :
                                                                                                                            item?.building_inspector_response ? (
                                                                                                                                item?.building_inspector_response
                                                                                                                            ) : (
                                                                                                                                <span className="text_empty text_italic">N/A</span>
                                                                                                                            )
                                                                                                                        }
                                                                                                                    </p>
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </React.Fragment>
                                                                                                    )}
                                                                                                </div>


                                                                                                {isInspector ? (
                                                                                                    <React.Fragment>
                                                                                                        {planCheckTrackerData?.stage_for === 'Response' && isSignedAndPublishedByBi === false && (
                                                                                                            <div className="pck_track_list_item_footer">
                                                                                                                <div className="pck_track_list_item_btn_group">
                                                                                                                    <button
                                                                                                                        className={`table_action_btn white bordered p-ripple`}
                                                                                                                        onClick={() => item?.building_inspector_response === null ? handleAddBiReply(item, part) : handleEditBiReply(item, part)}
                                                                                                                    >
                                                                                                                        {item?.building_inspector_response === null ? '' : 'Edit '}Reply
                                                                                                                        <Ripple />
                                                                                                                    </button>
                                                                                                                </div>
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </React.Fragment>
                                                                                                ) : isArchitect ? (
                                                                                                    <React.Fragment>
                                                                                                        {isSignedAndPublishedByBi === false && isSignedAndPublishedByArc === false && (
                                                                                                            <div className="pck_track_list_item_footer">
                                                                                                                <div className="pck_track_list_item_btn_group">
                                                                                                                    {item?.building_inspector_response === null && isSignedAndPublishedByBi === false && isSignedAndPublishedByArc === false && (
                                                                                                                        <button
                                                                                                                            className={`table_action_btn white bordered p-ripple`}
                                                                                                                            onClick={() => handleEditItem(item, part)}
                                                                                                                        >
                                                                                                                            Edit
                                                                                                                            <Ripple />
                                                                                                                        </button>
                                                                                                                    )}

                                                                                                                    <button
                                                                                                                        className={`table_action_btn white bordered p-ripple`}
                                                                                                                        onClick={() => item?.architect_response === null ? handleAddArcReply(item, part) : handleEditArcResponse(item, part)}
                                                                                                                    >
                                                                                                                        {item?.architect_response === null ? '' : 'Edit '}
                                                                                                                        {item?.building_inspector_response === null && isSignedAndPublishedByBi === false && isSignedAndPublishedByArc === false ? 'Response' : 'Reply'}
                                                                                                                        <Ripple />
                                                                                                                    </button>
                                                                                                                </div>
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </React.Fragment>
                                                                                                ) : null}
                                                                                            </div>
                                                                                        ))
                                                                                    ) : (
                                                                                        <div className="no_item_area">
                                                                                            <span>
                                                                                                {`No item has been created. ${isInspector ? 'To create the item click on add button.' : ''}`}
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </motion.div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </AnimatePresence>
                                                        </React.Fragment>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <NoDataCreated
                                    message={`No part has been created. ${isArchitect ? 'To create the part click on add button.' : ''}`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>


            {/* Plan check tracker part modal */}
            <Dialog
                visible={showPartModal}
                headerClassName="custom_modal_header"
                header={partModalHeader}
                footer={partModalFooter}
                dismissableMask
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleClosePartModal}>
                <div className="custom_modal_body">
                    <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                        <div className="row">
                            {/* Part number */}
                            <div className="col-12 col-sm-12">
                                <TextInput
                                    id="partNumber"
                                    label="Part number"
                                    labelHtmlFor="partNumber"
                                    required={true}
                                    value={partNumber}
                                    placeholder="Eg: Part A"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPartNumber(e.target.value)}
                                    error={partNumberError}
                                />
                            </div>

                            {/* Part title */}
                            <div className="col-12 col-sm-12">
                                <TextInput
                                    id="partTitle"
                                    label="Part title"
                                    labelHtmlFor="partTitle"
                                    required={true}
                                    value={partTitle}
                                    placeholder="Part title"
                                    formGroupClassName="mb-1"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPartTitle(e.target.value)}
                                    error={partTitleError}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Plan check tracker item modal */}
            <Dialog
                visible={showItemModal}
                headerClassName="custom_modal_header"
                header={itemModalHeader}
                footer={itemModalFooter}
                dismissableMask
                className="custom_modal_dialog modal_dialog_md"
                onHide={handleCloseItemModal}>
                <div className="custom_modal_body">
                    <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                        <div className="row">
                            {/* Part */}
                            {activePartData && activePartData !== null && (
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <h6 className="data_label">
                                            {activePartData?.part_number}:&nbsp;{activePartData?.part_title}
                                        </h6>
                                    </div>
                                </div>
                            )}

                            {/* Item code */}
                            <div className="col-12 col-sm-6">
                                <div className="page_form_group">
                                    <label htmlFor="itemCode" className="custom_form_label">
                                        Item code&nbsp;
                                        <small>
                                            {dataState === "Add"
                                                ? "(Auto generated)"
                                                : "(Can't modify)"}
                                        </small>
                                    </label>
                                    <InputText
                                        key={`itemCode`}
                                        id="itemCode"
                                        value={itemCode}
                                        className="custom_form_input"
                                        placeholder="Eg: 1"
                                        disabled
                                    />

                                    {itemCodeError && (
                                        <small className="form_error_msg">
                                            {itemCodeError}
                                        </small>
                                    )}
                                </div>
                            </div>

                            {/* Note */}
                            <div className="col-12">
                                <Textarea
                                    id="comment"
                                    label="Comment"
                                    labelHtmlFor="comment"
                                    required={true}
                                    value={comment}
                                    placeholder="Comment"
                                    formGroupClassName="mb-1"
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
                                    error={commentError}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Plan check architect response modal */}
            <Dialog
                visible={showArcReplyModal}
                headerClassName="custom_modal_header"
                header={arcReplyModalHeader}
                footer={arcReplyModalFooter}
                dismissableMask
                className="custom_modal_dialog modal_dialog_md"
                onHide={() => handleCloseArcReplyModal('Close')}
            >
                <div className="custom_modal_body">
                    <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                        {selectedItemData && selectedItemData !== null && (
                            <div className="row">
                                {/* Item */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <h6 className="data_label">
                                            {selectedItemData?.item_code}.&nbsp;{selectedItemData?.comment}
                                        </h6>
                                    </div>
                                </div>

                                {/* Response note */}
                                <div className="col-12">
                                    <Textarea
                                        id="arcResponseNote"
                                        label="Response note"
                                        labelHtmlFor="arcResponseNote"
                                        required={true}
                                        value={arcResponseNote}
                                        placeholder="Enter response note"
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setArcResponseNote(e.target.value)}
                                        error={arcResponseNoteError}
                                    />
                                </div>

                                {/* Response Files */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <label htmlFor="" className="custom_form_label">Files</label>

                                        <div className="checklist_img_container mt-2 mb-1">
                                            {dataState === 'Edit' && (
                                                <React.Fragment>
                                                    {selectedItemData?.file_list && selectedItemData?.file_list?.length > 0 && (
                                                        <React.Fragment>
                                                            {selectedItemData?.file_list?.map((file, index) => (
                                                                <div className="note_image_container response_image_container" key={file?.id}>
                                                                    {responseFileDeleteLoadingId === file?.id ? (
                                                                        <FileActionLoader />
                                                                    ) : (
                                                                        <React.Fragment>
                                                                            {file?.extension === "pdf" ? (
                                                                                <div
                                                                                    className="response_doc_file_container"
                                                                                    onClick={() => handleViewDocument(file?.path!)}
                                                                                >
                                                                                    <i className="bi bi-file-earmark-pdf"></i>
                                                                                </div>
                                                                            ) : (
                                                                                <ImagePreview
                                                                                    src={file?.path!}
                                                                                    alt={`arc_response_img_${index + 1}`}
                                                                                />
                                                                            )}
                                                                        </React.Fragment>
                                                                    )}

                                                                    <button
                                                                        className="img_clear_btn p-ripple"
                                                                        onClick={() => handleDeleteResponseFile(file?.id!)}
                                                                    >
                                                                        <i className="bi bi-x"></i>
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </React.Fragment>
                                                    )}
                                                </React.Fragment>
                                            )}

                                            {arcResponseFiles && arcResponseFiles?.length > 0 && (
                                                <React.Fragment>
                                                    {arcResponseFiles.map((file, index) => {
                                                        const fileExtension = file.name?.split(".").pop()?.toLowerCase();
                                                        return (
                                                            <div
                                                                className="note_image_container response_image_container"
                                                                key={index}
                                                            >
                                                                {fileExtension === "pdf" ? (
                                                                    <div
                                                                        className="response_doc_file_container"
                                                                        onClick={() => handleViewDocument(URL.createObjectURL(file))}
                                                                    >
                                                                        <i className="bi bi-file-earmark-pdf"></i>
                                                                    </div>
                                                                ) : (
                                                                    <ImagePreview
                                                                        src={URL.createObjectURL(file)}
                                                                        alt={`arc_response_img_${index + 1}`}
                                                                    />
                                                                )}

                                                                <button
                                                                    className="img_clear_btn p-ripple"
                                                                    onClick={() => handleRemoveResponseFile(index)}
                                                                >
                                                                    <i className="bi bi-x"></i>
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            )}

                                            <Tooltip target={`.img_upload_label`} />

                                            <label
                                                htmlFor="arcResponseFiles"
                                                className="img_upload_label p-ripple"
                                                data-pr-tooltip="Upload files"
                                                data-pr-position="right"
                                            >
                                                <i className="bi bi-upload"></i>
                                                <input
                                                    id="arcResponseFiles"
                                                    multiple
                                                    type="file"
                                                    accept={documentFileExtension + ', ' + imageFileExtension}
                                                    onChange={handleUploadResponseFiles}
                                                />
                                                <Ripple />
                                            </label>
                                        </div>

                                        <span className="file_upload_info pt-1">
                                            <i className="bi bi-info-circle"></i> Acceptable file format : {documentFileExtension + ', ' + imageFileExtension}
                                        </span>

                                        {arcResponseFileError &&
                                            <small className="form_error_msg">{arcResponseFileError}</small>
                                        }
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Plan check building inspector response modal */}
            <Dialog
                visible={showBiReplyModal}
                headerClassName="custom_modal_header"
                header={biReplyModalHeader}
                footer={biReplyModalFooter}
                dismissableMask
                className="custom_modal_dialog modal_dialog_md"
                onHide={() => handleCloseBiReplyModal('Close')}
            >
                <div className="custom_modal_body">
                    <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                        {selectedItemData && selectedItemData !== null && (
                            <div className="row">
                                {/* Item */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <h6 className="data_label">
                                            {selectedItemData?.item_code}.&nbsp;{selectedItemData?.comment}
                                        </h6>
                                    </div>
                                </div>

                                {/* Architect response */}
                                {selectedItemData?.architect_response && selectedItemData?.architect_response !== null && (
                                    <div className="col-12">
                                        <div className="page_form_group">
                                            <div className="response_input_area my-0 readonly bg_visible">
                                                <label>Architect Response :{" "}</label>

                                                <p className="first_letter_capitalized">
                                                    {selectedItemData?.architect_response ? (
                                                        selectedItemData?.architect_response
                                                    ) : (
                                                        <span className="text_empty text_italic">N/A</span>
                                                    )}
                                                </p>

                                                {selectedItemData?.file_list && selectedItemData?.file_list?.length > 0 && (
                                                    <div className="response_file_area mt-3 pb-0">
                                                        <div className="checklist_img_container">
                                                            {selectedItemData?.file_list?.map((selectedItemData, index) => (
                                                                <div
                                                                    className="note_image_container response_image_container"
                                                                    key={selectedItemData?.id}
                                                                >
                                                                    {selectedItemData?.extension === "pdf" ? (
                                                                        <div
                                                                            className="response_doc_file_container"
                                                                            onClick={() => handleViewDocument(selectedItemData?.path!)}
                                                                        >
                                                                            <i className="bi bi-file-earmark-pdf"></i>
                                                                        </div>
                                                                    ) : (
                                                                        <ImagePreview
                                                                            src={selectedItemData?.path!}
                                                                            alt={`arc_response_img_${index + 1}`}
                                                                        />
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Status */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <label htmlFor="biResponseStatus" className="custom_form_label is_required">Status</label>

                                        <div className="response_status_container px-0">
                                            <div className="response_status_grp">
                                                <RadioButton
                                                    inputId="Satisfactory"
                                                    value={biResponseStatus}
                                                    onChange={(e: RadioButtonChangeEvent) => setBiResponseStatus("Satisfactory")
                                                    }
                                                    checked={biResponseStatus === "Satisfactory"}
                                                />
                                                <label htmlFor="Satisfactory">
                                                    Satisfactory
                                                </label>
                                            </div>
                                            <div className="response_status_grp">
                                                <RadioButton
                                                    inputId="Unsatisfactory"
                                                    value={biResponseStatus}
                                                    onChange={(e: RadioButtonChangeEvent) =>
                                                        setBiResponseStatus("Unsatisfactory")
                                                    }
                                                    checked={biResponseStatus === "Unsatisfactory"}
                                                />
                                                <label htmlFor="Unsatisfactory">
                                                    Unsatisfactory
                                                </label>
                                            </div>
                                        </div>

                                        {biResponseStatusError &&
                                            <small className="form_error_msg">{biResponseStatusError}</small>
                                        }
                                    </div>
                                </div>

                                {/* Response note */}
                                <div className="col-12">
                                    <Textarea
                                        id="biResponseNote"
                                        label="Response note"
                                        labelHtmlFor="biResponseNote"
                                        required={false}
                                        value={biResponseNote}
                                        placeholder="Enter response note"
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setBiResponseNote(e.target.value)}
                                        error={biResponseNoteError}
                                        formGroupClassName="mb-1"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showItemDocViewModal}
                onClose={handleCloseFileViewModal}
                fileUrl={documentFilePath}
            />
            {/*  */}


            {/* Mail modal */}
            <Dialog
                header={mailModalHeader}
                footer={mailModalFooter}
                headerClassName="custom_modal_header"
                visible={showMailModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseMailModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                <TextInput
                                    id="emailAddress"
                                    label="E-mail address"
                                    labelHtmlFor="emailAddress"
                                    required={true}
                                    value={emailAddress}
                                    placeholder="abcd@gmail.com"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setEmailAddress(e.target.value)
                                    }
                                    error={emailAddressError}
                                    keyFilter={"email"}
                                    formGroupClassName="mb-2"
                                />

                                {planCheckTrackerData?.file_download_path !== null && (
                                    <div
                                        className="file_attached_area"
                                        onClick={handleViewReportDocument}
                                    >
                                        <i className="bi bi-paperclip"></i>
                                        <h6>Plan check tracker report.pdf</h6>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}
        </React.Fragment>
    )
}

export default PlanCheckTrackerDetail;